import { TimelineLite } from 'gsap';
import { DrawerBase } from 'bog-arcade-core-ui/core/DrawerBase';
import { debounce, delay } from 'bog-arcade-core-ui/helpers';

import { mobileConfig } from '../../../core/applicationConfig';
import { resourceHelper } from '../../../core/resourceHelper';
import { sharedSourceNames } from '../../../core/sourceNames';
import {
  handlePointerout,
  handlePointerover,
  onCenterDown,
  onCenterUp,
  onDragEnd,
  onDragMove,
  onDragStart,
} from '../events';
import {
  amountTextStyle,
  bonusActionStyle,
  bonusBlueElementTextStyle,
  bonusGreenElementTextStyle,
  bonusRedElementTextStyle,
  buyBonusTitleTextStyle,
  buyBonusValueTextStyle,
} from '../../../core/textStyles';
import { translationKeys } from '../../../core/translation/translation';
import { popupService } from '../../../services/helperServices/PopupService';
import { GameSceneType, TicketType } from '../../../core/enums';
import {
  BALANCE_VALUE, BONUS_COST_AMOUNT,
  FREE_SPIN_COUNT_STATE,
  GAME_APPLICATION_SCENE_STATE,
  MULTIPLIER_STATE,
  PRE_BONUS_ID,
} from '../../../stateManagment';
import { getAmount } from '../../../core/helpers/getAmount';
import { gameService } from '../../../services/apiServices/GameService';
import { userService } from '../../../services/helperServices/UserService';
import { getBallColorAndCrackState } from '../../../core/helpers/getBallColorAndCrackState';
import { ballDrawer } from '../../balls/BallDrawer';
import { soundService } from '../../../services/helperServices/SoundService';
import { gamePadService } from '../../../services/helperServices/GamePadService';
import { autoSpinHelper } from '../../../services/helperServices/AutoSpinService';


export class BaseBuyBonusPopup extends DrawerBase {
  constructor(props) {
    super(props);
    this.position = [];
    this.containers = [];
    this.scales = [.8, 1.1, .8];
    this.draggingConfig = {
      dragging: false,
      startX: null,
      data: null,
    };
    this.clickConfig = {
      startX: null,
    };
    this._slideLeft = debounce(this._slideLeft.bind(this), 200, true);
    this._slideRight = debounce(this._slideRight.bind(this), 200, true);
    this.isAnimationDone = false;
  }

  __resetDrag() {
    this.draggingConfig.dragging = false;
    this.draggingConfig.data = null;
    this.draggingConfig.startX = null;
  }

  _slideLeft(
    {
      duration = .2,
    } = {},
  ) {
    this.__resetDrag();
    this.selectContainer.removeChildren();
    const [leftScale, centerScale, rightScale] = this.scales;
    const [left, center, right] = this.containers;
    const [{ x: lX }, { x: cX }, { x: rX }] = this.positions;
    this.containers = [center, right, left];

    this.selectContainer.addChild(
      ...this.__getContainersByLayerLeftSide(),
    );

    new TimelineLite().to(left, { duration, x: rX, ease: 'none', pixi: { scale: rightScale } });
    new TimelineLite().to(center, { duration, x: lX, ease: 'none', pixi: { scale: leftScale } });
    new TimelineLite().to(right, { duration, x: cX, ease: 'none', pixi: { scale: centerScale } });
  }

  _slideRight(
    {
      duration = .2,
    } = {},
  ) {
    this.__resetDrag();
    this.selectContainer.removeChildren();
    const [leftScale, centerScale, rightScale] = this.scales;
    const [left, center, right] = this.containers;
    const [{ x: lX }, { x: cX }, { x: rX }] = this.positions;
    this.containers = [right, left, center];

    this.selectContainer.addChild(
      ...this.__getContainersByLayer(),
    );

    new TimelineLite().to(right, { duration, x: lX, ease: 'none', pixi: { scale: leftScale } });
    new TimelineLite().to(center, { duration, x: rX, ease: 'none', pixi: { scale: rightScale } });
    new TimelineLite().to(left, { duration, x: cX, ease: 'none', pixi: { scale: centerScale } });
  }

  _moveElementsByDifference(difference) {
    this.active_texture = null;
    if (difference > 1) {
      this._slideRight();
    }
    if (difference < -1) {
      this._slideLeft();
    }
  }

  _registerDragEvent({ texture, parent }) {
    texture
      .on('pointerdown', event => onDragStart(event, this, parent))
      .on('pointerup', event => onDragEnd(event, this))
      .on('pointerupoutside', event => onDragEnd(event, this))
      .on('pointermove', event => onDragMove(event, this, parent));
  }

  _registerClickEvent({ texture }) {
    this.registerClick(texture, (event) => {
      soundService.playSingleClick();
      if (popupService.buyBonusClickableState) {
        onCenterDown(event, this, { texture, parent: texture });
      }
    });

    this.registerPointerup(texture, event => {
      onCenterUp(event, this, { texture, parent: texture });
    });
  }

  __getContainersByLayer() {
    const [left, center, right] = this.containers;
    return [left, right, center];
  }

  __getContainersByLayerLeftSide() {
    const [left, center, right] = this.containers;
    return [right, left, center];
  }

  __setupTexture(texture, scale) {
    texture.anchor.set(.5);
    texture.scale.set(scale);
  }

  __moveContainers({ texture, duration, delay, from, to, ease = 'back.out(1.7)', onComplete }) {
    this.setPosition(texture, from);
    new TimelineLite().to(texture, {
      duration,
      delay,
      ease,
      ...to,
      onComplete,
    });
  }

  __setArrowAnimation({ duration = .5, texture }) {
    const { position: { y } } = texture;
    new TimelineLite()
      .to(texture, { duration, y: y + 20 })
      .to(texture, {
        duration, y, onComplete: () => {
          if (popupService.buyBonusPopupState && !popupService.buyBonusPopupConfirmState) {
            this.__setArrowAnimation({ duration, texture });
          }
        },
      });
  }

  getSelectContainer(
    {
      parentWidth = mobileConfig.width,
      parentHeight = mobileConfig.height,
      scale = 1,
    } = {},
  ) {
    this.scales = this.scales.map(s => s * scale);
    const selectContainer = this.createContainer('selectContainer');
    const ticketX1Container = this.createContainer('ticketX1Container');
    const ticketX3Container = this.createContainer('ticketX3Container');
    const ticketX5Container = this.createContainer('ticketX5Container');
    const halfParentWidth = parentWidth / 2;
    const halfParentHeight = parentHeight / 2;

    ticketX1Container.ticketType = TicketType.green;
    ticketX3Container.ticketType = TicketType.red;
    ticketX5Container.ticketType = TicketType.blue;

    this.positions = [
      { x: halfParentWidth - this.space, y: halfParentHeight },
      { x: halfParentWidth, y: halfParentHeight },
      { x: halfParentWidth + this.space, y: halfParentHeight },
    ];
    this.containers = [ticketX1Container, ticketX3Container, ticketX5Container];
    this.containers.forEach((container, index) => container.scale.set(this.scales[index]));

    const ticketX1Texture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX1));
    const ticketX3Texture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX3));
    const ticketX5Texture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX5));

    const ticketX1LightTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX1Light));
    const ticketX3LightTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX3Light));
    const ticketX5LightTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX5Light));

    const ticketX1BowTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX1Bow));
    const ticketX3BowTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX3Bow));
    const ticketX5BowTexture = this.createSprite(resourceHelper.get(sharedSourceNames.ticketX5Bow));

    const goldenArrowX1Texture = this.createSprite(resourceHelper.get(sharedSourceNames.goldenArrow));
    const goldenArrowX3Texture = this.createSprite(resourceHelper.get(sharedSourceNames.goldenArrow));
    const goldenArrowX5Texture = this.createSprite(resourceHelper.get(sharedSourceNames.goldenArrow));

    const ticketX1Title = this.createText(10, bonusGreenElementTextStyle);
    const ticketX1Value = this.createText('X1', bonusGreenElementTextStyle);

    const ticketX3Title = this.createText(5, bonusRedElementTextStyle);
    const ticketX3Value = this.createText('X3', bonusRedElementTextStyle);

    const ticketX5Title = this.createText(3, bonusBlueElementTextStyle);
    const ticketX5Value = this.createText('X5', bonusBlueElementTextStyle);

    ticketX1LightTexture.visible = false;
    ticketX3LightTexture.visible = false;
    ticketX5LightTexture.visible = false;

    const textureList = [
      ticketX1Texture,
      ticketX3Texture,
      ticketX5Texture,
      ticketX1LightTexture,
      ticketX3LightTexture,
      ticketX5LightTexture,
      ticketX1BowTexture,
      ticketX3BowTexture,
      ticketX5BowTexture,
      ticketX1Title,
      ticketX1Value,
      ticketX3Title,
      ticketX3Value,
      ticketX5Title,
      ticketX5Value,
      goldenArrowX1Texture,
      goldenArrowX3Texture,
      goldenArrowX5Texture,
    ];

    textureList.forEach(elem => this.__setupTexture(elem, scale));

    goldenArrowX1Texture.scale.set(scale * .30);
    goldenArrowX3Texture.scale.set(scale * .30);
    goldenArrowX5Texture.scale.set(scale * .30);

    ticketX1Container.addChild(
      ticketX1LightTexture,
      ticketX1BowTexture,
      ticketX1Texture,
      ticketX1Title,
      ticketX1Value,
    );

    ticketX3Container.addChild(
      ticketX3LightTexture,
      ticketX3BowTexture,
      ticketX3Texture,
      ticketX3Title,
      ticketX3Value,
    );

    ticketX5Container.addChild(
      ticketX5LightTexture,
      ticketX5BowTexture,
      ticketX5Texture,
      ticketX5Title,
      ticketX5Value,
    );

    if (gameService.isMobile) {
      ticketX1Container.addChild(goldenArrowX1Texture);
      ticketX3Container.addChild(goldenArrowX3Texture);
      ticketX5Container.addChild(goldenArrowX5Texture);
    }

    selectContainer.addChild(
      ...this.__getContainersByLayer(),
    );

    this.setupButtonMode(ticketX1Texture);
    this.setupButtonMode(ticketX3Texture);
    this.setupButtonMode(ticketX5Texture);

    this.registerPointerover(ticketX1Texture, () => {
      ticketX1LightTexture.visible = true;
    });
    this.registerPointerover(ticketX3Texture, () => {
      ticketX3LightTexture.visible = true;
    });
    this.registerPointerover(ticketX5Texture, () => {
      ticketX5LightTexture.visible = true;
    });

    this.registerPointerout(ticketX1Texture, () => {
      ticketX1LightTexture.visible = false;
    });
    this.registerPointerout(ticketX3Texture, () => {
      ticketX3LightTexture.visible = false;
    });
    this.registerPointerout(ticketX5Texture, () => {
      ticketX5LightTexture.visible = false;
    });

    this._registerDragEvent({ texture: selectContainer, parent: selectContainer });
    this._registerClickEvent({ texture: ticketX1Texture });
    this._registerClickEvent({ texture: ticketX3Texture });
    this._registerClickEvent({ texture: ticketX5Texture });

    this.addSubscriber(popupService.subscribeBuyBonusPopupState(state => {
      const [left, center, right] = this.containers;
      const [leftPosition, centerPosition, rightPosition] = this.positions;
      const time = .5;

      if (!popupService.buyBonusPopupConfirmState && state) {
        this.__moveContainers({
          texture: left,
          delay: 0,
          duration: time,
          from: {
            x: leftPosition.x,
            y: leftPosition.y - 1500,
          },
          to: leftPosition,
        });
        this.__moveContainers({
          texture: center,
          delay: time,
          duration: time,
          from: {
            x: centerPosition.x,
            y: centerPosition.y - 1500,
          },
          to: centerPosition,
          onComplete: () => {
            this.isAnimationDone = true;
          },
        });
        this.__moveContainers({
          texture: right,
          delay: time / 2,
          duration: time,
          from: {
            x: rightPosition.x,
            y: rightPosition.y - 1500,
          },
          to: rightPosition,
        });
      }
      if (!popupService.buyBonusPopupConfirmState && !state) {
        this.__moveContainers({
          texture: left,
          delay: 0,
          duration: time,
          ease: 'none',
          to: {
            x: leftPosition.x,
            y: leftPosition.y - 1500,
          },
          from: leftPosition,
        });
        this.__moveContainers({
          texture: center,
          delay: time,
          duration: time,
          ease: 'none',
          to: {
            x: centerPosition.x,
            y: centerPosition.y - 1500,
          },
          from: centerPosition,
        });
        this.__moveContainers({
          texture: right,
          delay: time / 2,
          duration: time,
          ease: 'none',
          to: {
            x: rightPosition.x,
            y: rightPosition.y - 1500,
          },
          from: rightPosition,
        });
      }
    }));

    this.setPosition(ticketX1Container, this.positions[0]);
    this.setPosition(ticketX3Container, this.positions[1]);
    this.setPosition(ticketX5Container, this.positions[2]);

    this.setPosition(ticketX1Title, { y: -95 * scale });
    this.setPosition(ticketX1Value, { y: 150 * scale });
    this.setPosition(ticketX3Title, { y: -95 * scale });
    this.setPosition(ticketX3Value, { y: 150 * scale });
    this.setPosition(ticketX5Title, { y: -95 * scale });
    this.setPosition(ticketX5Value, { y: 150 * scale });
    this.setPosition(goldenArrowX1Texture, { y: -350 * scale });
    this.setPosition(goldenArrowX3Texture, { y: -350 * scale });
    this.setPosition(goldenArrowX5Texture, { y: -350 * scale });


    this.addSubscriber(popupService.subscribeBuyBonusPopupState((state) => {
      if (state) {
        this.__setArrowAnimation({ texture: goldenArrowX1Texture });
        this.__setArrowAnimation({ texture: goldenArrowX3Texture });
        this.__setArrowAnimation({ texture: goldenArrowX5Texture });
      } else {
        this.setPosition(goldenArrowX1Texture, { y: -350 * scale });
        this.setPosition(goldenArrowX3Texture, { y: -350 * scale });
        this.setPosition(goldenArrowX5Texture, { y: -350 * scale });
      }
    }));

    this.addSubscriber(popupService.subscribeBuyBonusPopupConfirmState((state, { prev }) => {
      if (prev !== null && !state && gameService.isMobile) {
        this.__setArrowAnimation({ texture: goldenArrowX1Texture });
        this.__setArrowAnimation({ texture: goldenArrowX3Texture });
        this.__setArrowAnimation({ texture: goldenArrowX5Texture });
      }
    }));

    this.selectContainer = selectContainer;
    return selectContainer;
  }

  _registerConfirmContainerEvents(container, active, normal) {
    this.registerPointerover(
      container,
      (event) => handlePointerover(event, { active, normal }),
    );
    this.registerPointerout(
      container,
      (event) => handlePointerout(event, { active, normal }),
    );
  }

  getConfirmContainer(
    {
      scale = 1,
      parentWidth = mobileConfig.width,
      parentHeight = mobileConfig.height,
      mistakeY = 0,
    } = {},
  ) {
    const halfWidth = parentWidth / 2;
    const halfHeight = parentHeight / 2;
    const confirmContainer = this.createContainer('confirmContainer');
    const firstLineTextureContainer = this.createContainer('firstLineTextureContainer');
    const secondLineTextureContainer = this.createContainer('secondLineTextureContainer');

    const buyBonusPopupTexture = this.createSprite(resourceHelper.get(sharedSourceNames.buyBonusPopup));
    const buttonNoActiveTexture = this.createSprite(resourceHelper.get(sharedSourceNames.buttonNoActive));
    const buttonNoNormalTexture = this.createSprite(resourceHelper.get(sharedSourceNames.buttonNoNormal));
    const buttonYesActiveTexture = this.createSprite(resourceHelper.get(sharedSourceNames.buttonYesActive));
    const buttonYesNormalTexture = this.createSprite(resourceHelper.get(sharedSourceNames.buttonYesNormal));
    const lightEffect = this.createSpine(resourceHelper.get(sharedSourceNames.greenPopupLightSpine));
    lightEffect.scale.set(scale * .55);
    lightEffect.state.setAnimation(0, 'animation', true);
    buttonNoActiveTexture.alpha = 0;
    buttonYesActiveTexture.alpha = 0;

    const yesContainer = this.createContainer('yesContainer');
    const noContainer = this.createContainer('noContainer');

    const yesTextTexture = this.createText(translationKeys.yes, bonusActionStyle);
    const noTextTexture = this.createText(translationKeys.no, bonusActionStyle);

    const amountTexture = this.createText(`5000 FUN`, amountTextStyle);
    amountTexture.scale.set(scale * .55);
    amountTexture.anchor.set(.5);

    yesContainer.addChild(
      buttonYesActiveTexture,
      buttonYesNormalTexture,
      yesTextTexture,
    );

    noContainer.addChild(
      buttonNoActiveTexture,
      buttonNoNormalTexture,
      noTextTexture,
    );

    const textureList = [
      buyBonusPopupTexture,
      buttonNoActiveTexture,
      buttonNoNormalTexture,
      buttonYesActiveTexture,
      buttonYesNormalTexture,
      yesTextTexture,
      noTextTexture,
      amountTexture,
    ];

    textureList.forEach(elem => this.__setupTexture(elem, scale * .55));

    this.setupButtonMode(yesContainer);
    this.setupButtonMode(noContainer);

    this._registerConfirmContainerEvents(yesContainer, buttonYesActiveTexture, buttonYesNormalTexture);
    this._registerConfirmContainerEvents(noContainer, buttonNoActiveTexture, buttonNoNormalTexture);

    this.registerClick(noContainer, () => {
      soundService.playSingleClick();
      popupService.toggleBuyBonusPopupConfirmState();
    });

    this.registerClick(yesContainer,
      debounce(() => {
        if (userService.checkBalance(BONUS_COST_AMOUNT.getValue())) {
          autoSpinHelper.end();
          soundService.playSingleClick();
          popupService.disableBuyBonus();
          popupService.toggleBuyBonusPopupConfirmState();
          gameService.buyBonus().then((res) => {
            gamePadService.disableAll();
            if (gameService.isRealMode) {
              BALANCE_VALUE.next(res.balance);
            } else {
              userService.minusBalance(BONUS_COST_AMOUNT.getValue());
            }
            PRE_BONUS_ID.next(res);
            gameService.preBonus().then((response) => {
              const { colors, crackStates } = getBallColorAndCrackState(response.ball);
              popupService.closeBuyBonusPopup();
              delay(() => {
                GAME_APPLICATION_SCENE_STATE.next(GameSceneType.bonus);
                ballDrawer.application.setBonusGameBalls(colors, crackStates);
              }, 1200);
            });
          }).catch(() => {
            popupService.enableBuyBonus();
          });
        }
      }, 300, true),
    );

    this.addSubscriber(FREE_SPIN_COUNT_STATE.subscribe(value => {
      firstLineTextureContainer.removeChildren();
      const firstLineTexture = this.createTextContainer([
        this.createText(translationKeys.buy, buyBonusTitleTextStyle),
        this.createText(value, buyBonusValueTextStyle),
        this.createText(translationKeys.dragonSpins, buyBonusTitleTextStyle),
      ]);
      firstLineTexture.scale.set(scale * .55);
      firstLineTextureContainer.addChild(firstLineTexture);
    }, true));

    this.addSubscriber(MULTIPLIER_STATE.subscribe(value => {
      secondLineTextureContainer.removeChildren();
      const secondLineTexture = this.createTextContainer([
        this.createText(translationKeys.multiplierBeginsAt, buyBonusTitleTextStyle),
        this.createText(`X${value}`, buyBonusValueTextStyle),
      ]);
      secondLineTexture.scale.set(scale * .55);
      secondLineTextureContainer.addChild(secondLineTexture);
    }, true));

    this.addSubscriber(BONUS_COST_AMOUNT.subscribe(value => {
      amountTexture.text = getAmount(value);
    }, true));

    this.setPosition(amountTexture, { x: 0, y: +80 * scale });
    this.setPosition(buyBonusPopupTexture, { x: 0, y: 0 });
    this.setPosition(firstLineTextureContainer, { x: 0, y: -40 * scale });
    this.setPosition(secondLineTextureContainer, { x: 0, y: 10 * scale });
    this.setPosition(yesContainer, { x: +scale * 90, y: +scale * 175 });
    this.setPosition(noContainer, { x: -scale * 90, y: +scale * 175 });
    this.setPosition(confirmContainer, { x: halfWidth, y: halfHeight + mistakeY });

    confirmContainer.addChild(
      lightEffect,
      buyBonusPopupTexture,
      yesContainer,
      noContainer,
      firstLineTextureContainer,
      secondLineTextureContainer,
      amountTexture,
    );

    return confirmContainer;
  }

  getPopup(
    {
      scale = 1,
      parentWidth = mobileConfig.width,
      parentHeight = mobileConfig.height,
      mistakeY = 0,
    } = {},
  ) {
    const rootContainer = this.createContainer('root');
    const confirmContainer = this.getConfirmContainer({ scale, parentWidth, parentHeight, mistakeY });
    const selectContainer = this.getSelectContainer({ parentWidth, parentHeight, scale, mistakeY });

    const rect = this.createGraphics()
      .beginFill(0x00000)
      .drawRect(0, 0, parentWidth, parentHeight)
      .endFill();
    rect.alpha = .6;

    const disabledLayer = this.createGraphics()
      .beginFill(0x00000)
      .drawRect(0, 0, parentWidth, parentHeight)
      .endFill();
    disabledLayer.alpha = 0;
    const blurFilter = new this.PIXI.filters.BlurFilter();
    rect.filters = [blurFilter];
    blurFilter.blur = 40;


    this.setupButtonMode(rect);
    this.setupButtonMode(disabledLayer);
    this.setupButtonMode(selectContainer);

    this.registerClick(rect, () => {
      if (this.isAnimationDone) {
        soundService.playSingleClick();
        popupService.closeBuyBonusPopup();
        this.isAnimationDone = false;
      }
    });

    this.registerClick(disabledLayer, (event) => {
      soundService.playSingleClick();
      event.stopPropagation();
    });
    this.addSubscriber(popupService.subscribeBuyBonusPopupConfirmState((state) => {
      selectContainer.visible = !state;
      confirmContainer.visible = state;
      if (state) {
        confirmContainer.scale.set(0);
        new TimelineLite().to(confirmContainer, { duration: .3, pixi: { scale: 1 } });
      }
    }));

    rootContainer.addChild(
      rect,
      selectContainer,
      confirmContainer,
      disabledLayer,
    );

    this.addSubscriber(popupService.subscribeBuyBonusPopupState((state, { prev }) => {
      if (prev === null) {
        disabledLayer.visible = false;
        rootContainer.visible = state;
        return;
      }

      if (state) {
        disabledLayer.visible = false;
        rootContainer.visible = state;
      } else {
        disabledLayer.visible = true;
        if (popupService.buyBonusPopupConfirmState) {
          rootContainer.visible = state;
        } else {
          delay(() => {
            rootContainer.visible = state;
          }, 1200);
        }
      }
    }));

    return rootContainer;
  }
}
